<div class="header">
  <div>
    <h2>{{ "pages.admin.pricing.title" | translate }}</h2>
    <p class="subtitle">{{ "pages.admin.pricing.subtitle" | translate }}</p>
  </div>
  <div class="header__actions">
    <span class="save-state" role="status" aria-live="polite">
      @if (saving) {
        <nb-icon icon="loader-outline" class="al-spin"></nb-icon>{{ "pages.admin.pricing.offers.saving" | translate }}
      } @else if (!loading && !loadFailed) {
        <nb-icon icon="checkmark-outline"></nb-icon>{{ "pages.admin.pricing.offers.saved" | translate }}
      }
    </span>
    <button nbButton size="small" status="basic" type="button" (click)="reload(false, true)" [disabled]="loading || saving">
      <nb-icon icon="refresh-outline"></nb-icon>{{ "buttons.refresh" | translate }}
    </button>
  </div>
</div>

@if (loading) {
  <app-page-loader></app-page-loader>
} @else if (loadFailed) {
  <nb-card>
    <nb-card-body class="load-failed">
      <p>{{ "pages.admin.pricing.offers.loadFailed" | translate }}</p>
      <button nbButton size="small" status="primary" type="button" (click)="reload(true)">
        <nb-icon icon="refresh-outline"></nb-icon>{{ "buttons.refresh" | translate }}
      </button>
    </nb-card-body>
  </nb-card>
} @else {

<!-- Kennzahlen der Basis -->
<div class="kpi-head">
  <p class="kpi-caption">
    {{ "pages.admin.pricing.kpis.caption" | translate: { name: active?.name ?? "" } }}
    · {{ "pages.admin.pricing.kpis.aiCost." + (aiCostMeasured ? "measured" : "manual") | translate: { value: format(effective.llmPerCredit, "eur3"), days: measuredAi?.days ?? 0 } }}
  </p>
  @if (active) {
    <app-segmented-control
      [options]="caseOptions"
      [value]="active.usageCase"
      (valueChange)="setActiveCase($event)"
      [ariaLabel]="'pages.admin.pricing.fields.usageCase' | translate"
    ></app-segmented-control>
  }
</div>
<div class="kpis">
  @for (k of kpis; track k.key) {
    <div class="kpi al-glass al-glass--tile">
      <div class="kpi__body al-glass__body">
        <span class="kpi__label">{{ "pages.admin.pricing.kpis." + k.key | translate }}</span>
        <div class="kpi__values">
          @for (v of variants; track v) {
            <div class="kpi__value">
              <span class="kpi__variant">{{ "pages.admin.pricing.matrix.variants." + v | translate }}</span>
              @if (k.kind === "pct") {
                <span class="kpi__number" [ngClass]="'kpi__number--' + marginTone(k[v])">{{ format(k[v], "pct") }}</span>
              } @else {
                <span class="kpi__number" [class.kpi__number--error]="k[v] < 0">{{ format(k[v], "eur") }}</span>
              }
            </div>
          }
        </div>
      </div>
    </div>
  }
</div>

<!-- Szenarien vergleichen -->
<nb-card class="scenarios-card">
  <nb-card-header>
    <h6><nb-icon icon="layers-outline"></nb-icon>{{ "pages.admin.pricing.scenarios.title" | translate }}</h6>
  </nb-card-header>
  <nb-card-body>
    <div class="toolbar">
      <div class="toolbar__group">
        <span class="label">{{ "pages.admin.pricing.presets.label" | translate }}</span>
        <button nbButton size="small" status="basic" type="button" [disabled]="busy" (click)="applyPreset('enterprise')">
          {{ "pages.admin.pricing.presets.enterprise" | translate }}
        </button>
        <button nbButton size="small" status="basic" type="button" [disabled]="busy" (click)="applyPreset('list')">
          {{ "pages.admin.pricing.presets.list" | translate }}
        </button>
        <button nbButton size="small" status="basic" type="button" [disabled]="busy" (click)="applyPreset('empty')">
          {{ "pages.admin.pricing.presets.empty" | translate }}
        </button>
      </div>

      <div class="toolbar__group">
        <span class="label">{{ "pages.admin.pricing.variants.label" | translate }}</span>
        <nb-select size="small" class="variant-field" [selected]="variantField" (selectedChange)="variantField = $event">
          @for (f of axisFields; track f) {
            <nb-option [value]="f">{{ "pages.admin.pricing.fields." + f | translate }}</nb-option>
          }
        </nb-select>
        <input
          nbInput
          fieldSize="small"
          class="variant-values"
          [(ngModel)]="variantValues"
          [placeholder]="'pages.admin.pricing.variants.placeholder' | translate"
          (keydown.enter)="generateVariants()"
        />
        <button nbButton size="small" status="primary" type="button" (click)="generateVariants()" [disabled]="!variantValues.trim() || busy" [nbSpinner]="busy">
          {{ "pages.admin.pricing.variants.generate" | translate }}
        </button>
      </div>

      <div class="toolbar__group toolbar__group--end">
        <button nbButton size="small" status="basic" type="button" (click)="addScenario()" [disabled]="!canAdd">
          <nb-icon icon="plus-outline"></nb-icon>{{ "pages.admin.pricing.scenarios.add" | translate }}
        </button>
        <button nbButton size="small" status="basic" type="button" (click)="copyText()">
          <nb-icon icon="copy-outline"></nb-icon>{{ "pages.admin.pricing.export.copy" | translate }}
        </button>
        <button nbButton size="small" status="basic" type="button" (click)="downloadCsv()">
          <nb-icon icon="download-outline"></nb-icon>CSV
        </button>
      </div>
    </div>

    <div class="offer-picker" role="group" [attr.aria-label]="'pages.admin.pricing.offers.pickerAria' | translate">
      <span class="label">
        {{ "pages.admin.pricing.offers.picker" | translate: { n: scenarios.length, max: maxScenarios, total: offers.length } }}
      </span>
      <div class="offer-picker__list">
        @for (o of offers; track o.id) {
          @if (meta[o.id]; as m) {
            <button
              type="button"
              class="offer-pill"
              [class.is-on]="!m.hidden"
              [attr.aria-pressed]="!m.hidden"
              (click)="setHidden(o.id, !m.hidden)"
              [nbTooltip]="'pages.admin.pricing.offers.actions.' + (m.hidden ? 'load' : 'hide') | translate"
            >
              <span class="offer-pill__dot" [ngClass]="'offer-pill__dot--' + statusTone(m.status)"></span>
              <span class="offer-pill__name">{{ o.name }}</span>
              <nb-icon [icon]="m.hidden ? 'plus-outline' : 'checkmark-outline'"></nb-icon>
            </button>
          }
        }
      </div>
    </div>

    @if (!scenarios.length) {
      <p class="empty-compare">{{ "pages.admin.pricing.offers.emptyCompare" | translate }}</p>
    }
    <div class="table-scroll table-scroll--cmp" appScrollOverflow [hidden]="!scenarios.length">
      <table class="cmp">
        <thead>
          <tr>
            <th class="label-col" scope="col">
              <span class="hint">{{ "pages.admin.pricing.scenarios.activeHint" | translate }}</span>
            </th>
            @for (s of scenarios; track s.id) {
              <th scope="col" class="scenario-head" [class.is-active]="s.id === activeId" (click)="select(s)">
                <input
                  class="scenario-name"
                  [ngModel]="s.name"
                  (ngModelChange)="rename(s, $event)"
                  [attr.aria-label]="'pages.admin.pricing.scenarios.name' | translate"
                  (focus)="select(s)"
                />
                <div class="scenario-actions">
                  @if (meta[s.id]; as m) {
                    <button
                      type="button"
                      class="status-btn al-status-chip"
                      [ngClass]="'al-status-chip--' + statusTone(m.status)"
                      (click)="$event.stopPropagation(); openStatus(s.id)"
                      [nbTooltip]="m.status_reason || ('pages.admin.pricing.offers.actions.status' | translate)"
                    >
                      {{ "pages.admin.pricing.offers.status." + m.status | translate }}
                    </button>
                  }
                  @if (s.id === activeId) {
                    <span class="al-status-chip al-status-chip--info">{{ "pages.admin.pricing.scenarios.active" | translate }}</span>
                  }
                  <button
                    type="button"
                    class="icon-btn"
                    (click)="$event.stopPropagation(); setHidden(s.id, true)"
                    [nbTooltip]="'pages.admin.pricing.offers.actions.hide' | translate"
                  >
                    <nb-icon icon="eye-off-outline"></nb-icon>
                  </button>
                  <button
                    type="button"
                    class="icon-btn"
                    [disabled]="!canAdd"
                    (click)="$event.stopPropagation(); duplicate(s)"
                    [nbTooltip]="'pages.admin.pricing.scenarios.duplicate' | translate"
                  >
                    <nb-icon icon="copy-outline"></nb-icon>
                  </button>
                  <button
                    type="button"
                    class="icon-btn icon-btn--danger"
                    (click)="$event.stopPropagation(); remove(s)"
                    [nbTooltip]="'pages.admin.pricing.scenarios.remove' | translate"
                  >
                    <nb-icon icon="trash-2-outline"></nb-icon>
                  </button>
                </div>
              </th>
            }
          </tr>
        </thead>

        <tbody>
          <!-- Angebot -->
          <tr class="group-row">
            <th class="label-col" scope="rowgroup">{{ "pages.admin.pricing.groups.offer" | translate }}</th>
            <td [attr.colspan]="scenarios.length"></td>
          </tr>
          <tr>
            <th class="label-col" scope="row">{{ "pages.admin.pricing.offers.columns.customer" | translate }}</th>
            @for (s of scenarios; track s.id) {
              <td [class.is-active]="s.id === activeId">
                <input
                  class="offer-customer"
                  nbInput
                  fieldSize="small"
                  maxlength="200"
                  [ngModel]="meta[s.id]?.customer ?? ''"
                  (ngModelChange)="setCustomer(s, $event)"
                  (focus)="select(s)"
                />
              </td>
            }
          </tr>
          <tr>
            <th class="label-col" scope="row">{{ "pages.admin.pricing.offers.columns.reason" | translate }}</th>
            @for (s of scenarios; track s.id) {
              <td class="small reason" [class.is-active]="s.id === activeId">
                <button type="button" class="link-btn" (click)="openStatus(s.id)">
                  {{ meta[s.id]?.status_reason || ("pages.admin.pricing.offers.addReason" | translate) }}
                </button>
              </td>
            }
          </tr>

          <!-- Eingaben -->
          @for (g of inputGroups; track g.key) {
            <tr class="group-row">
              <th class="label-col" scope="rowgroup">{{ "pages.admin.pricing.groups." + g.key | translate }}</th>
              <td [attr.colspan]="scenarios.length"></td>
            </tr>
            @for (row of g.rows; track row.key) {
              <tr [class.is-different]="differs[row.key]">
                <th class="label-col" scope="row">
                  <span class="row-label">{{ "pages.admin.pricing.fields." + row.key | translate }}</span>
                  @if (scenarios.length > 1) {
                    <button
                      type="button"
                      class="icon-btn apply-all"
                      (click)="applyToAll(row.key)"
                      [nbTooltip]="'pages.admin.pricing.scenarios.applyAll' | translate"
                    >
                      <nb-icon icon="arrowhead-right-outline"></nb-icon>
                    </button>
                  }
                </th>
                @for (s of scenarios; track s.id) {
                  <td [class.is-active]="s.id === activeId">
                    @switch (row.kind) {
                      @case ('mode') {
                        <app-segmented-control
                          class="mode-switch"
                          [options]="modeOptions"
                          [value]="s.platformMode"
                          (valueChange)="update(s, 'platformMode', $event)"
                          [ariaLabel]="'pages.admin.pricing.fields.platformMode' | translate"
                        ></app-segmented-control>
                      }
                      @case ('case') {
                        <app-segmented-control
                          class="mode-switch mode-switch--cases"
                          [options]="caseOptions"
                          [value]="s.usageCase"
                          (valueChange)="update(s, 'usageCase', $event)"
                          [ariaLabel]="'pages.admin.pricing.fields.usageCase' | translate"
                        ></app-segmented-control>
                      }
                      @case ('sla') {
                        <nb-select
                          size="small"
                          class="sla-select"
                          [selected]="s.slaLevel"
                          (selectedChange)="update(s, 'slaLevel', $event)"
                          [optionsWidth]="300"
                        >
                          @for (l of slaLevels; track l.id) {
                            <nb-option [value]="l.id">{{ "pages.admin.pricing.sla.levels." + l.id + ".name" | translate }}</nb-option>
                          }
                        </nb-select>
                        @if (s.slaLevel !== "none") {
                          <span class="sla-info">{{ slaInfo(s.slaLevel) }}</span>
                        }
                      }
                      @case ('bool') {
                        <nb-checkbox [checked]="!!s[row.key]" (checkedChange)="update(s, row.key, $event)">
                          {{ "pages.admin.pricing.fields.creditTiersOn" | translate }}
                        </nb-checkbox>
                      }
                      @default {
                        @if (
                          (row.key === 'platformPerLearner' && s.platformMode === 'tiered') ||
                          (row.key === 'creditPrice' && s.creditTiers)
                        ) {
                          <span class="derived">
                            {{ row.key === "platformPerLearner"
                              ? format(results[s.id]?.platformPerLearnerEff ?? 0, "eur3") + " Ø"
                              : ("pages.admin.pricing.fields.creditTiersShort" | translate) }}
                          </span>
                        } @else {
                          <input
                            nbInput
                            fieldSize="small"
                            type="number"
                            min="0"
                            [step]="row.step ?? 1"
                            [ngModel]="s[row.key]"
                            (ngModelChange)="update(s, row.key, $event)"
                            (focus)="select(s)"
                          />
                        }
                      }
                    }
                  </td>
                }
              </tr>
            }
          }

          <!-- Kennzahlen -->
          <tr class="group-row">
            <th class="label-col" scope="rowgroup">{{ "pages.admin.pricing.groups.overview" | translate }}</th>
            <td [attr.colspan]="scenarios.length"></td>
          </tr>
          <tr>
            <th class="label-col" scope="row">{{ "pages.admin.pricing.results.usedCredits" | translate }}</th>
            @for (s of scenarios; track s.id) {
              <td class="num" [class.is-active]="s.id === activeId">{{ format(results[s.id]?.usedCredits ?? 0, "int") }}</td>
            }
          </tr>
          <tr>
            <th class="label-col" scope="row">{{ "pages.admin.pricing.results.extraCredits" | translate }}</th>
            @for (s of scenarios; track s.id) {
              <td class="num" [class.is-active]="s.id === activeId">{{ format(results[s.id]?.managed?.overUnits ?? 0, "int") }}</td>
            }
          </tr>
          @for (v of variants; track v) {
            <tr>
              <th class="label-col" scope="row">
                {{ "pages.admin.pricing.results.marginByCase" | translate: { variant: ("pages.admin.pricing.matrix.variants." + v | translate) } }}
              </th>
              @for (s of scenarios; track s.id) {
                <td class="num small" [class.is-active]="s.id === activeId">
                  @if (caseResults[s.id]; as cr) {
                    @for (c of usageCases; track c; let last = $last) {
                      <span [class.case-current]="c === s.usageCase">{{ format(cr[c][v].marginPct, "pct") }}</span>{{ last ? "" : " · " }}
                    }
                  }
                </td>
              }
            </tr>
          }
          <tr>
            <th class="label-col" scope="row">{{ "pages.admin.pricing.results.byokMinusManaged" | translate }}</th>
            @for (s of scenarios; track s.id) {
              <td class="num small" [class.is-active]="s.id === activeId">
                @if (caseResults[s.id]; as cr) {
                  @for (c of usageCases; track c; let last = $last) {
                    <span
                      [class.case-current]="c === s.usageCase"
                      [class.neg]="cr[c].byok.contributionMonthly - cr[c].managed.contributionMonthly < 0"
                      >{{ format(cr[c].byok.contributionMonthly - cr[c].managed.contributionMonthly, "eur") }}</span
                    >{{ last ? "" : " · " }}
                  }
                }
              </td>
            }
          </tr>
          <tr>
            <th class="label-col" scope="row">{{ "pages.admin.pricing.results.savingsVsList" | translate }}</th>
            @for (s of scenarios; track s.id) {
              <td class="num" [class.is-active]="s.id === activeId">{{ format(results[s.id]?.savingsVsListPct ?? 0, "pct") }}</td>
            }
          </tr>
          <tr>
            <th class="label-col" scope="row">{{ "pages.admin.pricing.results.platformForMargin" | translate }}</th>
            @for (s of scenarios; track s.id) {
              <td class="num small" [class.is-active]="s.id === activeId">
                @if (results[s.id]; as r) {
                  {{ format(r.platformForMargin.breakEven, "eur3") }} · {{ format(r.platformForMargin.m40, "eur3") }} ·
                  {{ format(r.platformForMargin.m50, "eur3") }}
                }
              </td>
            }
          </tr>

          <!-- Managed + BYOK -->
          @for (v of variants; track v) {
            <tr class="group-row group-row--variant">
              <th class="label-col" scope="rowgroup">{{ "pages.admin.pricing.variants." + v | translate }}</th>
              <td [attr.colspan]="scenarios.length"></td>
            </tr>
            @for (r of resultRows; track r.key) {
              <tr [class.is-strong]="r.strong">
                <th class="label-col" scope="row">{{ "pages.admin.pricing.results." + r.key | translate }}</th>
                @for (s of scenarios; track s.id) {
                  <td class="num" [class.is-active]="s.id === activeId">
                    @if (results[s.id]; as res) {
                      @if (r.key === "marginPct") {
                        <span class="al-status-chip" [ngClass]="'al-status-chip--' + marginTone(res[v].marginPct)">
                          {{ format(res[v].marginPct, "pct") }}
                        </span>
                      } @else {
                        {{ format(res[v][r.key], r.kind) }}
                      }
                    }
                  </td>
                }
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  </nb-card-body>
</nb-card>

<!-- Nutzungsfälle: Managed gegen BYOK -->
@if (active && caseResults[active.id]; as cr) {
  <nb-card>
    <nb-card-header>
      <h6><nb-icon icon="options-2-outline"></nb-icon>{{ "pages.admin.pricing.usage.title" | translate: { name: active.name } }}</h6>
    </nb-card-header>
    <nb-card-body>
      <p class="matrix-hint">{{ "pages.admin.pricing.usage.hint" | translate }}</p>
      <div class="table-scroll" appScrollOverflow>
        <table class="cmp cases">
          <thead>
            <tr>
              <th class="label-col" scope="col"></th>
              @for (c of usageCases; track c) {
                <th scope="col" class="scenario-head case-head" [class.is-active]="c === active.usageCase" (click)="setActiveCase(c)">
                  <span class="case-name">{{ "pages.admin.pricing.usage.cases." + c | translate }}</span>
                  <input
                    class="case-input"
                    nbInput
                    fieldSize="small"
                    type="number"
                    min="0"
                    step="0.5"
                    [ngModel]="active[usageField[c]]"
                    (ngModelChange)="update(active, usageField[c], $event)"
                    (click)="$event.stopPropagation()"
                    [attr.aria-label]="'pages.admin.pricing.fields.' + usageField[c] | translate"
                  />
                  <span class="case-usage">{{ "pages.admin.pricing.usage.unit" | translate }}</span>
                </th>
              }
            </tr>
          </thead>
          <tbody>
            <tr>
              <th class="label-col" scope="row">{{ "pages.admin.pricing.results.usedCredits" | translate }}</th>
              @for (c of usageCases; track c) {
                <td class="num" [class.is-active]="c === active.usageCase">{{ format(cr[c].usedCredits, "int") }}</td>
              }
            </tr>
            <tr>
              <th class="label-col" scope="row">{{ "pages.admin.pricing.results.extraCredits" | translate }}</th>
              @for (c of usageCases; track c) {
                <td class="num" [class.is-active]="c === active.usageCase">{{ format(cr[c].managed.overUnits, "int") }}</td>
              }
            </tr>
            @for (v of variants; track v) {
              <tr class="group-row group-row--variant">
                <th class="label-col" scope="rowgroup">{{ "pages.admin.pricing.variants." + v | translate }}</th>
                <td colspan="3"></td>
              </tr>
              @for (key of caseRowKeys; track key) {
                <tr [class.is-strong]="key === 'contributionMonthly'">
                  <th class="label-col" scope="row">{{ "pages.admin.pricing.results." + key | translate }}</th>
                  @for (c of usageCases; track c) {
                    <td class="num" [class.is-active]="c === active.usageCase">
                      @if (key === "marginPct") {
                        <span class="al-status-chip" [ngClass]="'al-status-chip--' + marginTone(cr[c][v].marginPct)">
                          {{ format(cr[c][v].marginPct, "pct") }}
                        </span>
                      } @else {
                        {{ format(cr[c][v][key], "eur") }}
                      }
                    </td>
                  }
                </tr>
              }
            }
            <tr class="group-row">
              <th class="label-col" scope="rowgroup">{{ "pages.admin.pricing.usage.compare" | translate }}</th>
              <td colspan="3"></td>
            </tr>
            <tr class="is-strong">
              <th class="label-col" scope="row">{{ "pages.admin.pricing.results.byokMinusManagedMonth" | translate }}</th>
              @for (c of usageCases; track c) {
                <td class="num" [class.is-active]="c === active.usageCase">
                  <span class="al-status-chip" [ngClass]="'al-status-chip--' + (cr[c].byok.contributionMonthly >= cr[c].managed.contributionMonthly ? 'accept' : 'error')">
                    {{ format(cr[c].byok.contributionMonthly - cr[c].managed.contributionMonthly, "eur") }}
                  </span>
                </td>
              }
            </tr>
            <tr>
              <th class="label-col" scope="row">{{ "pages.admin.pricing.usage.better" | translate }}</th>
              @for (c of usageCases; track c) {
                <td class="num" [class.is-active]="c === active.usageCase">
                  {{ "pages.admin.pricing.matrix.variants." + (cr[c].byok.contributionMonthly >= cr[c].managed.contributionMonthly ? "byok" : "managed") | translate }}
                </td>
              }
            </tr>
          </tbody>
        </table>
      </div>
    </nb-card-body>
  </nb-card>
}

<!-- Matrix -->
<nb-card>
  <nb-card-header>
    <h6><nb-icon icon="grid-outline"></nb-icon>{{ "pages.admin.pricing.matrix.title" | translate: { name: active?.name ?? "" } }}</h6>
  </nb-card-header>
  <nb-card-body>
    <div class="matrix-controls">
      @for (axis of ["row", "col"]; track axis) {
        <div class="axis">
          <span class="label">{{ "pages.admin.pricing.matrix." + axis + "Axis" | translate }}</span>
          <nb-select
            size="small"
            [selected]="axis === 'row' ? rowAxis.field : colAxis.field"
            (selectedChange)="setAxisField($any(axis), $event)"
          >
            @for (f of axisFields; track f) {
              <nb-option [value]="f">{{ "pages.admin.pricing.fields." + f | translate }}</nb-option>
            }
          </nb-select>
          <label class="axis__num">
            <span>{{ "pages.admin.pricing.matrix.start" | translate }}</span>
            <input
              nbInput
              fieldSize="small"
              type="number"
              [ngModel]="axis === 'row' ? rowAxis.start : colAxis.start"
              (ngModelChange)="updateAxis($any(axis), 'start', $event)"
            />
          </label>
          <label class="axis__num">
            <span>{{ "pages.admin.pricing.matrix.step" | translate }}</span>
            <input
              nbInput
              fieldSize="small"
              type="number"
              [ngModel]="axis === 'row' ? rowAxis.step : colAxis.step"
              (ngModelChange)="updateAxis($any(axis), 'step', $event)"
            />
          </label>
          <label class="axis__num">
            <span>{{ "pages.admin.pricing.matrix.count" | translate }}</span>
            <input
              nbInput
              fieldSize="small"
              type="number"
              min="1"
              max="20"
              [ngModel]="axis === 'row' ? rowAxis.count : colAxis.count"
              (ngModelChange)="updateAxis($any(axis), 'count', $event)"
            />
          </label>
        </div>
      }
      <div class="matrix-switches">
        <app-segmented-control
          [options]="metricOptions"
          [value]="matrixMetric"
          (valueChange)="setMetric($event)"
          [ariaLabel]="'pages.admin.pricing.matrix.metric' | translate"
        ></app-segmented-control>
        <app-segmented-control
          [options]="variantOptions"
          [value]="matrixVariant"
          (valueChange)="setVariant($event)"
          [ariaLabel]="'pages.admin.pricing.matrix.variant' | translate"
        ></app-segmented-control>
      </div>
    </div>

    <p class="matrix-hint">{{ "pages.admin.pricing.matrix.hint" | translate }}</p>

    <div class="table-scroll" appScrollOverflow>
      <table class="matrix">
        <thead>
          <tr>
            <th class="corner" scope="col">
              {{ "pages.admin.pricing.fields." + rowAxis.field | translate }} ↓ ·
              {{ "pages.admin.pricing.fields." + colAxis.field | translate }} →
            </th>
            @for (cv of matrix.colValues; track $index) {
              <th scope="col" class="num">{{ formatAxis(colAxis.field, cv) }}</th>
            }
          </tr>
        </thead>
        <tbody>
          @for (row of matrix.cells; track $index; let ri = $index) {
            <tr>
              <th scope="row" class="num">{{ formatAxis(rowAxis.field, matrix.rowValues[ri]) }}</th>
              @for (cell of row; track cell.col) {
                <td>
                  <button
                    type="button"
                    class="cell"
                    [ngClass]="'cell--' + cellTone(cell.value, cell.marginPct)"
                    [class.cell--base]="cell.isBase"
                    (click)="takeCell(cell.row, cell.col)"
                  >
                    {{ formatMetric(cell.value) }}
                  </button>
                </td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>
    <div class="legend">
      @if (matrixVariant !== "diff") {
        <span class="al-status-chip al-status-chip--accept">≥ 50 %</span>
        <span class="al-status-chip al-status-chip--info">30–50 %</span>
        <span class="al-status-chip al-status-chip--warning">0–30 %</span>
        <span class="al-status-chip al-status-chip--error">&lt; 0 %</span>
        <span class="legend__text">{{ "pages.admin.pricing.matrix.legendMargin" | translate }}</span>
      } @else {
        <span class="legend__text">{{ "pages.admin.pricing.matrix.legendDiff" | translate }}</span>
      }
    </div>
  </nb-card-body>
</nb-card>

<!-- Kurve -->
<nb-card>
  <nb-card-header>
    <h6><nb-icon icon="trending-up-outline"></nb-icon>{{ "pages.admin.pricing.chart.title" | translate: { name: active?.name ?? "" } }}</h6>
  </nb-card-header>
  <nb-card-body>
    <div echarts [options]="chartOption" [autoResize]="true" theme="alphabees" class="chart"></div>
  </nb-card-body>
</nb-card>

<!-- Alle Angebote -->
<nb-card>
  <nb-card-header>
    <h6><nb-icon icon="archive-outline"></nb-icon>{{ "pages.admin.pricing.offers.title" | translate }}</h6>
  </nb-card-header>
  <nb-card-body>
    <p class="matrix-hint">{{ "pages.admin.pricing.offers.hint" | translate }}</p>
    <app-segmented-control
      class="status-filter"
      [options]="statusFilterOptions"
      [value]="statusFilter"
      (valueChange)="setStatusFilter($event)"
      [ariaLabel]="'pages.admin.pricing.offers.columns.status' | translate"
    ></app-segmented-control>
    <app-table
      [title]="'pages.admin.pricing.offers.title' | translate"
      [data]="offerRows"
      [columns]="offerColumns"
      [rowActions]="offerActions"
      [selectable]="false"
      [maxVisibleItems]="15"
      (event)="onOfferTableEvent($event)"
    ></app-table>
  </nb-card-body>
</nb-card>

<!-- Kostenannahmen -->
<nb-card>
  <nb-card-header>
    <h6><nb-icon icon="settings-2-outline"></nb-icon>{{ "pages.admin.pricing.costs.title" | translate }}</h6>
  </nb-card-header>
  <nb-card-body>
    <details class="al-disclosure">
      <summary>
        {{ "pages.admin.pricing.costs.summary" | translate }}
        @if (costsUpdatedBy) {
          <span class="costs__by">{{ "pages.admin.pricing.costs.updatedBy" | translate: { name: costsUpdatedBy } }}</span>
        }
      </summary>
      <div class="ai-source">
        <span class="label">{{ "pages.admin.pricing.costs.aiSource.label" | translate }}</span>
        <app-segmented-control
          [options]="aiSourceOptions"
          [value]="costs.llmSource"
          (valueChange)="setAiSource($event)"
          [ariaLabel]="'pages.admin.pricing.costs.aiSource.label' | translate"
        ></app-segmented-control>
        @if (measuredAi; as m) {
          <p class="ai-source__line">
            {{ "pages.admin.pricing.costs.aiSource.line" | translate: {
              cost: format(m.costPerCredit, "eur3"),
              margin: format(m.marginPct ?? 0, "pct"),
              overhead: format(m.overheadSharePct ?? 0, "pct"),
              credits: format(m.credits, "int"),
              date: (m.windowStart | date: "dd.MM.yyyy")
            } }}
            <a routerLink="/admin/token-analytics">{{ "pages.admin.pricing.costs.aiSource.link" | translate }}</a>
          </p>
          @if (m.unpricedRuns > 0) {
            <p class="ai-source__warn">{{ "pages.admin.pricing.costs.aiSource.unpriced" | translate: { n: m.unpricedRuns } }}</p>
          }
        } @else {
          <p class="ai-source__warn">{{ "pages.admin.pricing.costs.aiSource.none" | translate }}</p>
        }
      </div>
      <div class="costs">
        @for (c of costFields; track c.key) {
          <label class="costs__field">
            <span class="label">{{ "pages.admin.pricing.costs.fields." + c.key | translate }}</span>
            <input
              nbInput
              fieldSize="small"
              type="number"
              min="0"
              [step]="c.step"
              [ngModel]="c.key === 'llmPerCredit' ? effective[c.key] : costs[c.key]"
              (ngModelChange)="updateCost(c.key, $event)"
              [disabled]="c.key === 'llmPerCredit' && aiCostMeasured"
            />
            <span class="costs__hint">{{ "pages.admin.pricing.costs.hints." + c.key | translate }}</span>
          </label>
        }
      </div>
      <div class="costs__actions">
        <button nbButton size="small" status="basic" type="button" (click)="resetCosts()">
          <nb-icon icon="refresh-outline"></nb-icon>{{ "pages.admin.pricing.costs.reset" | translate }}
        </button>
      </div>
    </details>
  </nb-card-body>
</nb-card>

}
